// src/features/tabs/PaneChrome.tsx
// The channel a page uses to hang its own controls in the navbar.
//
// Why not props: a page is mounted several levels below the shell, inside a
// router of its own, and the navbar is a distant sibling — there is no prop path
// between them. A portal into a slot the navbar publishes keeps the controls in
// the page that owns them (its state, its handlers) while they paint in the one
// row that is already on screen, instead of a second row underneath it.
//
// They used to hang in the pane's tab strip. A strip is where tabs live: the
// controls filled it until the tab's own label had to be rescued by a scroller.
// The navbar has the room, and it already carries the breadcrumb — the other
// thing that says what you are looking at.
//
// Exactly one page may claim the slot: the visible tab of the focused pane.
// Dormant tabs stay mounted so their scroll position and half-filled forms
// survive a tab switch, and a split has a second pane on screen at the same
// time — without the gate they would all render into the same row at once.

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Where the shell's navbar takes page controls. Published by AppShell, read by
 * the panes so each can decide whether it is the one entitled to it.
 */
const NavbarSlotContext = createContext<HTMLElement | null>(null);

export const NavbarSlotProvider = NavbarSlotContext.Provider;

/** The navbar's slot, for a pane deciding what to pass on to its page. */
export function useNavbarSlot(): HTMLElement | null {
  return useContext(NavbarSlotContext);
}

/**
 * The slot as the page sees it. Three states, and the difference matters:
 * an element to portal into, `null` for "you are in a pane that is not the one
 * on duty", and `undefined` for "you are not in a pane at all".
 */
const PaneControlsContext = createContext<HTMLElement | null | undefined>(undefined);

export const PaneControlsProvider = PaneControlsContext.Provider;

/**
 * Renders `children` in the navbar, on behalf of the page that owns them.
 *
 * A page outside the workspace — a dialog, a test, the mobile shell — has no
 * pane above it and renders its controls in place rather than losing them. A
 * page in a pane that is not on duty renders nothing: its controls belong to
 * the navbar or nowhere, never inline above its own content.
 */
export function PaneControls({ children }: { children: ReactNode }) {
  const slot = useContext(PaneControlsContext);
  if (slot === undefined) return <>{children}</>;
  if (slot === null) return null;
  // A portal keeps React context: the controls still read the workspace state
  // from the provider above the page, even though they paint above it.
  return createPortal(children, slot);
}
